import { useAssessments } from '@/app/redux/hooks/assessments';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import { ControllerType } from '@/common/types/student';
import { MouseLeftClickIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { TableCell, TableRow } from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import dayjs from 'dayjs';
import { Info, Keyboard } from 'lucide-react';

interface PlacementTestRowProps {
  placementTest: PlacementTest;
  onViewResult: (result: PlacementTestResult) => void;
}

export default function PlacementTestRow({
  placementTest,
  onViewResult,
}: PlacementTestRowProps) {
  const { placementTestResults } = useAssessments();
  const result = placementTestResults.find(
    (r) => r.placementTestId === placementTest.id,
  );

  function getStudentStatus() {
    return placementTest.completed ? (
      <Badge className="bg-rag-green">Completed</Badge>
    ) : (
      <Badge variant="secondary">Not Started</Badge>
    );
  }

  function getStudentAction() {
    if (placementTest.completed && result)
      return (
        <Button variant="link" onClick={() => onViewResult(result)}>
          View Results
        </Button>
      );
    return null;
  }

  return (
    <TableRow
      className={cn(result && 'cursor-pointer')}
      onClick={() => result && onViewResult(result)}
    >
      <TableCell className="pl-4 text-sm w-[180px]">
        <div className="flex gap-2">
          {dayjs(placementTest.createdAt).format('DD/MM/YY h:mm a')}
          <Tooltip>
            <TooltipTrigger>
              <Info color="hsl(var(--primary))" size={16} />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <strong>Legacy Placement Test</strong>
              <br />
              Placement Tests have been replaced with Assessments and can no
              longer be completed, but you can still view results for existing
              ones.
            </TooltipContent>
          </Tooltip>
        </div>
      </TableCell>
      <TableCell className="pl-4">{getStudentStatus()}</TableCell>
      <TableCell className="pl-4">
        <Select value={placementTest.phase.toString()} disabled={true}>
          <SelectTrigger id="phase">
            <span className="font-medium">Phase {placementTest.phase}</span>
          </SelectTrigger>
        </Select>
      </TableCell>
      <TableCell className="pl-4">
        <Select value={placementTest.control} disabled={true}>
          <SelectTrigger id="control">
            <SelectValue placeholder="Select control type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ControllerType.KEYBOARD}>
              <span className="flex items-center gap-2">
                <Keyboard size={16} />
                Typing
              </span>
            </SelectItem>
            <SelectItem value={ControllerType.CLICK}>
              <span className="flex items-center gap-2">
                <MouseLeftClickIcon size={16} />
                Click to Place
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="pl-4">
        <Label
          className="flex items-center gap-2 text-sm text-muted-foreground w-fit"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={placementTest.placeAtAssessedLevel}
            disabled={true}
          />
          <span>Place automatically</span>
        </Label>
      </TableCell>
      <TableCell className="w-[100px]">{getStudentAction()}</TableCell>
    </TableRow>
  );
}
